import React, { useState, useEffect, useCallback } from 'react'
import {
  Box, Card, CardContent, Grid, TextField, Button, MenuItem,
  Select, FormControl, InputLabel, Typography, Accordion,
  AccordionSummary, AccordionDetails, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, Paper,
  TablePagination, IconButton, Tooltip, Chip, Dialog,
  DialogTitle, DialogContent, DialogActions, CircularProgress,
  Divider, Stack
} from '@mui/material'
import SearchIcon from '@mui/icons-material/Search'
import ClearIcon from '@mui/icons-material/Clear'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import VisibilityIcon from '@mui/icons-material/Visibility'
import EditIcon from '@mui/icons-material/Edit'
import DeleteIcon from '@mui/icons-material/Delete'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useSnackbar } from 'notistack'
import documentoService from '../services/documentoService'
import normaService from '../services/normaService'
import authService from '../services/authService'
import PageHeader from '../components/common/PageHeader'
import ConfirmDialog from '../components/common/ConfirmDialog'

const ROWS_PER_PAGE = 15

export default function TablaNormas() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { enqueueSnackbar } = useSnackbar()

  const [userRol, setUserRol] = useState('PUBLIC')
  const [userDataArea, setUserDataArea] = useState('')
  const [tiposNormasPorOficina, setTiposNormasPorOficina] = useState([])
  const [tiposNormasEditables, setTiposNormasEditables] = useState([])
  const [voces, setVoces] = useState([])

  // Filtros
  const [filterTipo, setFilterTipo] = useState('')
  const [filterNumero, setFilterNumero] = useState('')
  const [filterResena, setFilterResena] = useState('')
  const [filterFechaDesde, setFilterFechaDesde] = useState('')
  const [filterFechaHasta, setFilterFechaHasta] = useState('')
  const [filterCaracter, setFilterCaracter] = useState('')
  const [filterVoz, setFilterVoz] = useState('')

  // Tabla
  const [rows, setRows] = useState([])
  const [totalRecords, setTotalRecords] = useState(0)
  const [page, setPage] = useState(0)
  const [loading, setLoading] = useState(false)
  const [showTable, setShowTable] = useState(false)

  // PDF viewer
  const [pdfDialogOpen, setPdfDialogOpen] = useState(false)
  const [pdfUrl, setPdfUrl] = useState(null)

  // Detalle
  const [detailDialogOpen, setDetailDialogOpen] = useState(false)
  const [selectedNorma, setSelectedNorma] = useState(null)
  const [copyButtonCopied, setCopyButtonCopied] = useState(false)
  const [showOcr, setShowOcr] = useState(false)
  const [ocrTextCache, setOcrTextCache] = useState('')
  const [ocrLoading, setOcrLoading] = useState(false)

  // Confirmar eliminación
  const [confirmOpen, setConfirmOpen] = useState(false)
  const [normaToDelete, setNormaToDelete] = useState(null)

  useEffect(() => {
    const rol = authService.getUserRol()
    const area = authService.getUserDataArea()[0]
    setUserRol(rol)
    setUserDataArea(area)

    // cargar tipos de normas
    normaService.getTiposNormas().then(res => {
      const list = [{ id: 'TODAS', descripcion: 'TODAS' }, ...(res.data?.data || [])]
      setTiposNormasPorOficina(list)
    }).catch(() => {})

    // cargar voces
    normaService.getVoces().then(res => {
      const list = [{ id: '', descripcion: 'TODAS' }, ...(res.data?.data || [])]
      setVoces(list)
    }).catch(() => {})

    // si usuario login, cargar tipos editables
    if (rol !== 'PUBLIC') {
      normaService.getOficinas(null, area).then(res => {
        const data = res.data?.data || []
        if (data.length > 0) {
          normaService.getTiposNormasPorOficina(data[0].id).then(res2 => {
            setTiposNormasEditables((res2.data?.data || []).map(t => t.id_tipo_nl))
          }).catch(() => {})
        }
      }).catch(() => {})
    }

    // Si viene ?nl=xxx abrir PDF directamente
    const nlParam = searchParams.get('nl')
    if (nlParam) openPdfById(nlParam)
  }, [])

  const buildCriterios = (paginaActual = page) => ({
    idTipo: filterTipo && filterTipo !== 'TODAS' ? filterTipo : '',
    numero: filterNumero,
    resena: filterResena,
    fechaDesde: filterFechaDesde,
    fechaHasta: filterFechaHasta,
    caracter: filterCaracter,
    idVoz: filterVoz,
    rows: ROWS_PER_PAGE,
    first: paginaActual * ROWS_PER_PAGE,
  })

  const buscar = useCallback(async (paginaActual = 0) => {
    console.log('buscar', userRol)
    setLoading(true)
    setShowTable(true)
    try {
      const res = await documentoService.getNormasLegales(buildCriterios(paginaActual))
      const data = res.data?.data || []
      setRows(data)
      setTotalRecords(data[0]?.total_records || data.length)
      setPage(paginaActual)
    } catch {
      enqueueSnackbar('Error al buscar normas legales', { variant: 'error' })
    } finally {
      setLoading(false)
    }
  }, [filterTipo, filterNumero, filterResena, filterFechaDesde, filterFechaHasta, filterCaracter, filterVoz])

  const limpiarFiltros = () => {
    setFilterTipo('')
    setFilterNumero('')
    setFilterResena('')
    setFilterFechaDesde('')
    setFilterFechaHasta('')
    setFilterCaracter('')
    setFilterVoz('')
    setRows([])
    setTotalRecords(0)
    setShowTable(false)
    setPage(0)
  }

  const openPdfById = async (idPdf) => {
    try {
      const res = await documentoService.getDocumento(idPdf)
      const blob = new Blob([res.data], { type: 'application/pdf' })
      setPdfUrl(URL.createObjectURL(blob))
      setPdfDialogOpen(true)
    } catch {
      enqueueSnackbar('No se pudo cargar el documento PDF', { variant: 'error' })
    }
  }

  // Abre el detalle: la fila de la tabla ya trae los campos de vista; el OCR se pide al hacer click en "Texto Automático"
  const openDetailDialog = (norma) => {
    setSelectedNorma(norma)
    setCopyButtonCopied(false)
    setOcrTextCache('')
    setDetailDialogOpen(true)
  }

  const handleEdit = (norma) => {
    normaService.setNormaLegalToUpdate(norma.id, norma.id_tipo_nl, norma.numero)
    navigate('/nueva')
  }

  const handleDeleteRequest = (norma) => {
    setNormaToDelete(norma)
    setConfirmOpen(true)
  }

  const handleDeleteConfirm = async () => {
    if (!normaToDelete) return
    setConfirmOpen(false)
    try {
      await documentoService.deleteNormaLegal(normaToDelete.id)
      enqueueSnackbar('Norma legal eliminada correctamente', { variant: 'success' })
      buscar(page)
    } catch {
      enqueueSnackbar('Error al eliminar la norma legal', { variant: 'error' })
    }
    setNormaToDelete(null)
  }

  const canEdit = (norma) => tiposNormasEditables.includes(norma.id_tipo_nl)

  // ---------- NEW FUNCTIONS FOR THE BUTTONS ----------
  // Copia al portapapeles un link directo a la norma: {FRONT_URL}?nl={id_pdf}
  // Cambia el texto del botón a "Link copiado!" con color verde suave por 3 segundos
  const copyLink = () => {
    if (!selectedNorma?.id_pdf) {
      enqueueSnackbar('No hay enlace disponible para copiar', { variant: 'warning' })
      return
    }
    const frontUrl = (typeof window !== 'undefined' && window.location.origin
      ? window.location.origin.replace(/\/$/, '')
      : import.meta.env.VITE_SERVER_FRONT_ADDR || '')
    const link = `${frontUrl}/?nl=${selectedNorma.id_pdf}`

    const finish = () => {
      setCopyButtonCopied(true)
      setTimeout(() => setCopyButtonCopied(false), 3000)
    }
    const fail = () => enqueueSnackbar('No se pudo copiar el enlace', { variant: 'error' })

    if (navigator.clipboard && typeof navigator.clipboard.writeText === 'function') {
      navigator.clipboard.writeText(link).then(finish).catch(fail)
      return
    }
    // Fallback para navegadores sin soporte (Firefox/Safari en HTTP, iframes, etc.)
    try {
      const textarea = document.createElement('textarea')
      textarea.value = link
      textarea.setAttribute('readonly', '')
      textarea.style.position = 'fixed'
      textarea.style.left = '-9999px'
      document.body.appendChild(textarea)
      textarea.select()
      document.execCommand('copy')
      document.body.removeChild(textarea)
      finish()
    } catch {
      fail()
    }
  }

  // Muestra el texto extraído por OCR del PDF en un modal.
  // El OCR no viene en la lista, así que lo pedimos al backend on-demand.
  // La respuesta del backend es: data: [{ norma_legal: { ... ocr_text, ... }, ... }]
  // El dialogo de OCR se abre ENCIMA del detalle (no lo cierra); al cerrar el OCR
  // el detalle sigue visible para poder volver a el sin volver a abrir.
  const showOcrText = () => {
    if (!selectedNorma) return
    if (ocrTextCache) {
      setShowOcr(true)
      return
    }
    setOcrLoading(true)
    documentoService.getNormaById(selectedNorma.id).then(res => {
      const wrapper = res.data?.data || res.data
      const fullNorma = Array.isArray(wrapper)
        ? wrapper[0]?.norma_legal || wrapper[0] || selectedNorma
        : wrapper?.norma_legal || wrapper || selectedNorma
      const text = fullNorma?.ocr_text || ''
      if (!text) {
        enqueueSnackbar('No hay texto extraído disponible para esta norma', { variant: 'info' })
        setOcrLoading(false)
        return
      }
      setOcrTextCache(text)
      setSelectedNorma(prev => ({ ...prev, ...fullNorma }))
      setShowOcr(true)
      setOcrLoading(false)
    }).catch(() => {
      enqueueSnackbar('No se pudo obtener el texto extraído', { variant: 'error' })
      setOcrLoading(false)
    })
  }
  // --------------------------------------------------

  const formatDate = (value) => {
    if (!value) return '—'

    const parsed = new Date(value)
    if (!Number.isNaN(parsed.getTime())) {
      return new Intl.DateTimeFormat('es-AR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      }).format(parsed)
    }

    const isoLike = String(value).match(/^(\d{4})-(\d{2})-(\d{2})/)
    if (isoLike) {
      const [, year, month, day] = isoLike
      return `${day}/${month}/${year}`
    }

    return String(value)
  }

  return (
    <Box>
      <PageHeader
        title="Búsqueda de Normas Legales"
        subtitle="Poder Judicial de Mendoza"
        icon={SearchIcon}
      />

      {/* Filtros de búsqueda */}
      {!showTable && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth size="small">
                  <InputLabel>Tipo de Norma Legal</InputLabel>
                  <Select
                    value={filterTipo}
                    label="Tipo de Norma Legal"
                    onChange={e => setFilterTipo(e.target.value)}
                  >
                    {tiposNormasPorOficina.map(t => (
                      <MenuItem key={t.id} value={t.id}>{t.descripcion}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth size="small"
                  label="Número"
                  value={filterNumero}
                  onChange={e => setFilterNumero(e.target.value)}
                  inputProps={{ maxLength: 15 }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth size="small"
                  label='Texto (para buscar por frase exacta, use comillas: "Perez Juan")'
                  value={filterResena}
                  onChange={e => setFilterResena(e.target.value)}
                  inputProps={{ maxLength: 50 }}
                />
              </Grid>

              {/* Búsqueda avanzada */}
              <Grid item xs={12}>
                <Accordion elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: '8px !important' }}>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography variant="subtitle2" sx={{ color: '#0062A9' }}>Búsqueda avanzada</Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth size="small"
                          label="Fecha Desde (dd/mm/yyyy)"
                          value={filterFechaDesde}
                          onChange={e => setFilterFechaDesde(e.target.value)}
                          placeholder="dd/mm/yyyy"
                          inputProps={{ maxLength: 10 }}
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth size="small"
                          label="Fecha Hasta (dd/mm/yyyy)"
                          value={filterFechaHasta}
                          onChange={e => setFilterFechaHasta(e.target.value)}
                          placeholder="dd/mm/yyyy"
                          inputProps={{ maxLength: 10 }}
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <FormControl fullWidth size="small">
                          <InputLabel>Carácter</InputLabel>
                          <Select value={filterCaracter} label="Carácter" onChange={e => setFilterCaracter(e.target.value)}>
                            <MenuItem value="">TODAS</MenuItem>
                            <MenuItem value="individual">INDIVIDUAL</MenuItem>
                            <MenuItem value="general">GENERAL</MenuItem>
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <FormControl fullWidth size="small">
                          <InputLabel>Voz</InputLabel>
                          <Select value={filterVoz} label="Voz" onChange={e => setFilterVoz(e.target.value)}>
                            {voces.map(v => (
                              <MenuItem key={v.id} value={v.id}>{v.descripcion}</MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Grid>
                    </Grid>
                  </AccordionDetails>
                </Accordion>
              </Grid>

              <Grid item xs={12}>
                <Stack direction="row" spacing={2} justifyContent="center">
                  <Button
                    variant="outlined"
                    startIcon={<ClearIcon />}
                    onClick={limpiarFiltros}
                  >
                    Limpiar
                  </Button>
                  <Button
                    variant="contained"
                    startIcon={<SearchIcon />}
                    onClick={() => buscar(0)}
                    sx={{
                      bgcolor: '#6CD1F7',
                      '&:hover': {
                        bgcolor: '#5BB8E3',
                      },
                    }}
                  >
                    Buscar
                  </Button>
                </Stack>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      )}

      {/* Tabla de resultados */}
      {showTable && (
        <Box>
          <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
            <Button
              variant="outlined"
              startIcon={<ArrowBackIcon />}
              onClick={() => { setShowTable(false) }}
            >
              Volver a búsqueda
            </Button>
            <Typography variant="body2" color="text.secondary">
              {totalRecords} resultado{totalRecords !== 1 ? 's' : ''} encontrado{totalRecords !== 1 ? 's' : ''}
            </Typography>
          </Stack>

          <Paper sx={{ width: '100%', overflow: 'hidden' }}>
            <TableContainer>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ width: '19%' }}>Tipo</TableCell>
                    <TableCell sx={{ width: '8%' }}>Número</TableCell>
                    <TableCell sx={{ width: '8%' }}>Fecha</TableCell>
                    <TableCell sx={{ width: '15%' }}>Voz</TableCell>
                    <TableCell sx={{ width: '32%' }}>Tema</TableCell>
                    <TableCell sx={{ width: '8%' }} align="center">Acciones</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                        <CircularProgress size={28} />
                      </TableCell>
                    </TableRow>
                  ) : rows.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} align="center" sx={{ py: 3 }}>
                        <Typography color="text.secondary">No se encontraron normas legales</Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    rows.map((norma, idx) => (
                      <TableRow key={norma.id || idx} hover>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>{norma.descripcion_tipo_nl}</Typography>
                        </TableCell>
                        <TableCell>{norma.numero}</TableCell>
                        <TableCell>{formatDate(norma.fecha_firma)}</TableCell>
                        <TableCell>{norma.descripcion_voz_nl}</TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>
                            {norma.resena_short || norma.resena}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Stack direction="row" spacing={0.5} justifyContent="center">
                            <Tooltip title="Ver PDF">
                              <IconButton size="small" color="primary" onClick={() => openPdfById(norma.id_pdf)}>
                                <PictureAsPdfIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Ver detalle">
                              <IconButton size="small" onClick={() => openDetailDialog(norma)}>
                                <VisibilityIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            {/* Botones de editar/eliminar ocultos (descomentar para mostrar)
                            {userRol !== 'PUBLIC' && canEdit(norma) && (
                              <>
                                <Tooltip title="Editar">
                                  <IconButton size="small" color="warning" onClick={() => handleEdit(norma)}>
                                    <EditIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Eliminar">
                                  <IconButton size="small" color="error" onClick={() => handleDeleteRequest(norma)}>
                                    <DeleteIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              </>
                            )} */}
                          </Stack>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              component="div"
              count={totalRecords}
              page={page}
              rowsPerPage={ROWS_PER_PAGE}
              onPageChange={(_, p) => buscar(p)}
              rowsPerPageOptions={[ROWS_PER_PAGE]}
              labelDisplayedRows={({ from, to, count }) => `${from}–${to} de ${count}`}
            />
          </Paper>
        </Box>
      )}

      {/* Dialog: Detalle de norma */}
      <Dialog open={detailDialogOpen} onClose={() => setDetailDialogOpen(false)} maxWidth="sm" fullWidth sx={{ '& .MuiDialog-paper': { minWidth: 700 } }}>
        <DialogTitle>Detalle de Norma Legal</DialogTitle>
        <DialogContent dividers>
          {selectedNorma && (
            <Grid container spacing={1.5}>
              {[
                ['Tipo', selectedNorma.descripcion_tipo_nl],
                ['Número', selectedNorma.numero],
                ['Fecha de Firma', selectedNorma.fecha_firma],
                ['Fecha de Publicación', selectedNorma.fecha_publicacion],
                ['Carácter', selectedNorma.caracter],
                ['Voz', selectedNorma.descripcion_voz_nl],
                ['Unidad Organizativa', selectedNorma.descripcion_uo],
                ['Oficina', selectedNorma.descripcion_oficina],
                ['Tema/Reseña', selectedNorma.resena],
              ].map(([label, value]) => (
                <Grid item xs={12} key={label}>
                  <Typography variant="caption" color="text.secondary" display="block">{label}</Typography>
                  <Typography variant="body2">{value || '—'}</Typography>
                  <Divider sx={{ mt: 0.5 }} />
                </Grid>
              ))}
            </Grid>
          )}
        </DialogContent>

        <DialogActions sx={{ justifyContent: 'space-between', px: 2, py: 1.5, gap: 1 }}>
          {/* Acciones principales de la norma */}
          <Stack direction="row" spacing={1.5} alignItems="center" sx={{ flexWrap: 'nowrap' }}>
            <Button
              variant={copyButtonCopied ? 'contained' : 'outlined'}
              size="small"
              onClick={copyLink}
              sx={{ whiteSpace: 'nowrap' }}
            >
              {copyButtonCopied ? 'Link copiado!' : 'Copiar link de Norma Legal'}
            </Button>
            <Button variant="outlined" size="small" onClick={showOcrText} sx={{ whiteSpace: 'nowrap' }}>Texto Automático</Button>
            {selectedNorma?.id_pdf && (
              <Button size="small" startIcon={<PictureAsPdfIcon />} onClick={() => openPdfById(selectedNorma.id_pdf)} sx={{ whiteSpace: 'nowrap' }}>
                Ver PDF
              </Button>
            )}
          </Stack>

          {/* Cerrar */}
          <Stack direction="row" spacing={1.5} alignItems="center" sx={{ flexWrap: 'nowrap' }}>
            <Button onClick={() => setDetailDialogOpen(false)} variant="contained" size="small" sx={{ minWidth: 90, whiteSpace: 'nowrap' }}>
              Cerrar
            </Button>
          </Stack>
        </DialogActions>
      </Dialog>

      {/* Dialog: Texto automático (OCR extraído del PDF) */}
      <Dialog open={showOcr} onClose={() => setShowOcr(false)} maxWidth="md" fullWidth>
        <DialogTitle>Texto Extraido Automáticamente del Documento PDF</DialogTitle>
        <DialogContent dividers>
          {ocrLoading ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 3 }}>
              <CircularProgress size={24} />
              <Typography>Cargando texto extraído...</Typography>
            </Box>
          ) : (
            <Box
              component="pre"
              sx={{
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
                fontFamily: 'monospace',
                fontSize: '0.85rem',
                m: 0,
                p: 1,
                bgcolor: 'grey.100',
                borderRadius: 1,
                maxHeight: '60vh',
                overflow: 'auto',
              }}
            >
              {ocrTextCache ? ocrTextCache.trimStart() : ''}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" onClick={() => {
            if (ocrTextCache) {
              navigator.clipboard.writeText(ocrTextCache.trimStart())
                .then(() => enqueueSnackbar('Texto copiado al portapapeles', { variant: 'success' }))
                .catch(() => enqueueSnackbar('No se pudo copiar el texto', { variant: 'error' }))
            }
          }}>
            Copiar texto
          </Button>
          <Button onClick={() => setShowOcr(false)} variant="contained">Cerrar</Button>
        </DialogActions>
      </Dialog>

      {/* Dialog: PDF Viewer */}
      <Dialog open={pdfDialogOpen} onClose={() => { setPdfDialogOpen(false); if (pdfUrl) URL.revokeObjectURL(pdfUrl) }} maxWidth="lg" fullWidth>
        <DialogTitle>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">Documento PDF</Typography>
            <Button onClick={() => { setPdfDialogOpen(false); if (pdfUrl) URL.revokeObjectURL(pdfUrl) }}>
              Cerrar
            </Button>
          </Stack>
        </DialogTitle>
        <DialogContent sx={{ p: 0, height: '80vh' }}>
          {pdfUrl && (
            <iframe
              src={pdfUrl}
              title="PDF"
              style={{ width: '100%', height: '100%', border: 'none' }}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Confirmar eliminación */}
      <ConfirmDialog
        open={confirmOpen}
        title="Eliminar Norma Legal"
        message={`¿Confirma la eliminación de la norma ${normaToDelete?.descripcion_tipo_nl} N° ${normaToDelete?.numero}?`}
        onConfirm={handleDeleteConfirm}
        onCancel={() => { setConfirmOpen(false); setNormaToDelete(null) }}
      />
    </Box>
  )
}
